import { Injectable, inject } from '@angular/core';
import * as moment from 'moment';

import { CustomFieldsService } from '@main-application/management/pages/property-model/services/custom-fields.service';
import { LocalStorageDataEnum } from '@shared/enums/local-storage-data.enum';

import { CustomField, EmojiDetails, ResidentInspectionItem, SurveyPage } from '../pages/inspect/inspect.model';

@Injectable({
  providedIn: 'root',
})
export class ResidentInspectionsService {
  private customFieldsService = inject(CustomFieldsService);

  generateAnswer(items: ResidentInspectionItem[], itemUuid: string): string {
    const updatedSurvey = items[0]?.surveyJSON;
    if (!updatedSurvey) return;

    items.forEach((updatedItem: ResidentInspectionItem) => {
      const matchingPage = updatedSurvey.pages.find((page: SurveyPage) => page.uuid === updatedItem.uuid);
      if (!matchingPage) return;

      matchingPage.elements.forEach((element, elementIndex) => {
        switch (element.type?.toLowerCase()) {
          case 'comment':
            matchingPage.elements[elementIndex].value =
              updatedItem.uuid === itemUuid ? updatedItem.comment || ' ' : updatedItem.comment || '';
            break;

          case 'boolean':
            if (updatedItem.uuid === itemUuid) {
              const updatedBoolean = updatedItem.booleanQuestions?.find(q => q.guid === element.guid);
              if (updatedBoolean && updatedBoolean.answer !== 'undefined') {
                matchingPage.elements[elementIndex].value = String(updatedBoolean.answer) || '';
              }
            }
            break;

          case 'propertystate':
            if (updatedItem.uuid === itemUuid && element.items) {
              updatedItem.emojiDetails?.forEach((updatedEmoji: EmojiDetails) => {
                const matchingItem = element.items.find(
                  (itemWrapper: ResidentInspectionItem) =>
                    itemWrapper.item.emoji === updatedEmoji.emojiCode && updatedEmoji.isDefault
                );
                if (matchingItem?.item.id) {
                  matchingPage.elements[elementIndex].value = String(matchingItem.item.id);
                }
              });
            }
            break;

          case 'file':
            if (Array.isArray(updatedItem.fileUploadIds)) {
              matchingPage.mainImage = updatedItem.fileUploadIds.map(id => ({ fileUploadId: id }));
            }
            break;

          case 'text':
          case 'number':
          case 'int':
          case 'radiolist':
          case 'checklist':
          case 'dropdown': {
            const allCustoms = [
              ...(updatedItem.texts || []),
              ...(updatedItem.numbers || []),
              ...(updatedItem.ints || []),
              ...(updatedItem.moneys || []),
              ...(updatedItem.dropdowns || []),
            ];
            const updatedCustom = allCustoms.find(f => f.guid === element.guid);
            if (updatedCustom) {
              matchingPage.elements[elementIndex].value = updatedCustom.value ?? null;
            }
            break;
          }

          case 'money': {
            const updatedMoney = (updatedItem.moneys || []).find(f => f.guid === element.guid);
            if (updatedMoney) {
              matchingPage.elements[elementIndex].value = updatedMoney.value
                ? String(this.customFieldsService.parseAmount(updatedMoney.value))
                : undefined;
            }
            break;
          }

          case 'date': {
            const updatedDate = updatedItem.dates?.find(f => f.guid === element.guid);
            if (updatedDate?.value) {
              matchingPage.elements[elementIndex].value = moment(updatedDate.value).toISOString();
            }
            break;
          }

          case 'datetime': {
            const updatedDateTime = updatedItem.dateTimes?.find(f => f.guid === element.guid);
            if (updatedDateTime?.valueDate && updatedDateTime?.valueTime) {
              const merged = moment(updatedDateTime.valueDate)
                .set({
                  hour: moment(updatedDateTime.valueTime).hour(),
                  minute: moment(updatedDateTime.valueTime).minute(),
                  second: moment(updatedDateTime.valueTime).second(),
                })
                .toISOString();
              matchingPage.elements[elementIndex].value = merged;
            }
            break;
          }
        }
      });
    });

    return JSON.stringify(updatedSurvey, (key, value) => (Array.isArray(value) && value?.length === 0 ? null : value));
  }

  generateDefaultAnswer(items: ResidentInspectionItem[]): string {
    const defaultSurvey = items[0]?.surveyJSON;
    if (!defaultSurvey) return;

    items.forEach((defaultItem: ResidentInspectionItem) => {
      const matchingPage = defaultSurvey.pages.find((page: SurveyPage) => page.uuid === defaultItem.uuid);
      if (!matchingPage) return;

      matchingPage.elements.forEach((element, elementIndex) => {
        switch (element.type?.toLowerCase()) {
          case 'comment':
            if (defaultItem.comment) {
              matchingPage.elements[elementIndex].value = defaultItem.comment;
            }
            break;

          case 'boolean': {
            const updatedBoolean = defaultItem.booleanQuestions?.find(q => q.guid === element.guid);
            if (
              updatedBoolean?.answer !== undefined &&
              updatedBoolean?.answer !== null &&
              updatedBoolean?.answer !== ''
            ) {
              matchingPage.elements[elementIndex].value = String(updatedBoolean.answer);
            } else if (
              updatedBoolean?.defaultAnswer !== undefined &&
              updatedBoolean?.defaultAnswer !== null &&
              updatedBoolean?.defaultAnswer !== ''
            ) {
              matchingPage.elements[elementIndex].value = String(updatedBoolean.defaultAnswer);
            }
            break;
          }

          case 'propertystate':
            if (element.items) {
              let matchingEmojiSet = false;
              defaultItem.emojiDetails?.forEach((updatedEmoji: EmojiDetails) => {
                const matchingItem = element.items.find(
                  (itemWrapper: ResidentInspectionItem) =>
                    itemWrapper.item.emoji === updatedEmoji.emojiCode && updatedEmoji.isDefault
                );
                if (matchingItem?.item.id) {
                  matchingPage.elements[elementIndex].value = String(matchingItem.item.id);
                  matchingEmojiSet = true;
                }
              });
              if (!matchingEmojiSet) {
                matchingPage.elements[elementIndex].value = matchingPage.elements[elementIndex].value || null;
              }
            }
            break;

          case 'file':
            if (Array.isArray(defaultItem.fileUploadIds) && defaultItem.fileUploadIds.length > 0) {
              matchingPage.mainImage = defaultItem.fileUploadIds.map(id => ({ fileUploadId: id }));
            }
            break;

          case 'text':
          case 'number':
          case 'int':
          case 'checklist':
          case 'radiolist':
          case 'dropdown': {
            const allCustoms = [
              ...(defaultItem.texts || []),
              ...(defaultItem.numbers || []),
              ...(defaultItem.ints || []),
              ...(defaultItem.moneys || []),
              ...(defaultItem.dropdowns || []),
            ];
            const updatedCustom = allCustoms.find(f => f.guid === element.guid);
            if (updatedCustom?.value !== undefined && updatedCustom?.value !== null && updatedCustom?.value !== '') {
              matchingPage.elements[elementIndex].value = updatedCustom.value;
            }
            break;
          }

          case 'money': {
            const defaultMoney = (defaultItem.moneys || []).find(f => f.guid === element.guid);
            if (defaultMoney?.value !== undefined && defaultMoney?.value !== null && defaultMoney?.value !== '') {
              matchingPage.elements[elementIndex].value = defaultMoney.value
                ? String(this.customFieldsService.parseAmount(defaultMoney.value))
                : undefined;
            }
            break;
          }

          case 'date': {
            const updatedDate = defaultItem.dates?.find(f => f.guid === element.guid);
            if (updatedDate?.value) {
              matchingPage.elements[elementIndex].value = moment(updatedDate.value).toISOString();
            }
            break;
          }

          case 'datetime': {
            const updatedDateTime = defaultItem.dateTimes?.find(f => f.guid === element.guid);
            if (updatedDateTime?.valueDate && updatedDateTime?.valueTime) {
              const merged = moment(updatedDateTime.valueDate)
                .set({
                  hour: moment(updatedDateTime.valueTime).hour(),
                  minute: moment(updatedDateTime.valueTime).minute(),
                  second: moment(updatedDateTime.valueTime).second(),
                })
                .toISOString();
              matchingPage.elements[elementIndex].value = merged;
            }
            break;
          }
        }
      });
    });

    return JSON.stringify(defaultSurvey, (key, value) => (Array.isArray(value) && value?.length === 0 ? null : value));
  }

  getCompletedItem(item: ResidentInspectionItem): boolean {
    const hasValidValue = item.emojiDetails?.length === 0 || !!item.value;
    const chosenEmoji = item.emojiDetails?.find(emoji => emoji.isDefault);
    const { isCommentRequiredByLists, isPhotoRequiredByLists } = this.getRequirementsBasedOnLists(item);

    if (!hasValidValue) return false;

    const allBooleanQuestionsAnswered =
      item.booleanQuestions?.length === 0 ||
      item.booleanQuestions.every(q => q.answer === 'true' || q.answer === 'false');

    if (!allBooleanQuestionsAnswered) return false;

    const allCustomFieldsAnswered =
      (item.texts?.length === 0 || item.texts.every(this.hasAnyAnswer)) &&
      (item.numbers?.length === 0 || item.numbers.every(this.hasAnyAnswer)) &&
      (item.ints?.length === 0 || item.ints.every(this.hasAnyAnswer)) &&
      (item.moneys?.length === 0 || item.moneys.every(this.hasAnyAnswer)) &&
      (item.dropdowns?.length === 0 || item.dropdowns.every(this.hasAnyAnswer)) &&
      (item.radiolists?.length === 0 || item.radiolists.every(this.hasAnyAnswer)) &&
      (item.checklists?.length === 0 || item.checklists.every(this.hasAnyAnswer)) &&
      (item.dates?.length === 0 || item.dates.every(this.hasAnyAnswer)) &&
      (item.dateTimes?.length === 0 || item.dateTimes.every(this.hasAnyAnswer));

    if (!allCustomFieldsAnswered) return false;

    let emojiRequiresPhoto = false;
    let emojiRequiresComment = false;

    if (item.emojiDetails?.length > 0) {
      emojiRequiresPhoto = chosenEmoji?.onSelect?.includes('photoRequired') || false;
      emojiRequiresComment = chosenEmoji?.onSelect?.includes('commentRequired') || false;
    } else {
      emojiRequiresPhoto = item.photoRequired;
      emojiRequiresComment = item.commentRequired;
    }

    const photoRequired = emojiRequiresPhoto || isPhotoRequiredByLists;
    const commentRequired = emojiRequiresComment || isCommentRequiredByLists;

    const hasValidFileUploads = photoRequired ? item.fileUploadIds?.length > 0 : true;

    if (!hasValidFileUploads) return false;

    const hasValidComment = commentRequired ? item.comment && item.comment.trim() !== '' : true;

    if (!hasValidComment) return false;

    const hasAnything =
      item.booleanQuestions?.length === 0 &&
      item.emojiDetails?.length === 0 &&
      !item.texts?.length &&
      !item.numbers?.length &&
      !item.ints?.length &&
      !item.moneys?.length &&
      !item.dropdowns?.some(e => !!e.value) &&
      !item.radiolists?.some(e => !!e.value) &&
      !item.checklists?.some(e => !!e.value) &&
      !item.dates?.length &&
      !item.dateTimes?.length
        ? item.fileUploadIds?.length > 0 || !!item.comment
        : true;

    return (
      hasValidValue &&
      allBooleanQuestionsAnswered &&
      allCustomFieldsAnswered &&
      hasValidFileUploads &&
      hasValidComment &&
      hasAnything
    );
  }

  isNotReadyToSubmitItem(item: ResidentInspectionItem): boolean {
    const hasValue =
      item.emojiDetails?.length === 0
        ? true
        : item.emojiDetails?.length > 0
        ? item.emojiDetails?.some((emoji: EmojiDetails) => emoji.isDefault === true)
        : false;

    if (!hasValue) return true;

    const { isCommentRequiredByLists, isPhotoRequiredByLists } = this.getRequirementsBasedOnLists(item);
    const selectedEmoji = item.emojiDetails?.find((emoji: EmojiDetails) => emoji.isDefault === true);
    const isPhotoVisible = item.photoVisible !== false;
    const isCommentVisible = item.commentVisible !== false;

    const hasAnswers =
      item.booleanQuestions?.length === 0
        ? true
        : item.booleanQuestions?.length > 0
        ? item.booleanQuestions.every(q => q.answer === 'true' || q.answer === 'false') ||
          item.booleanQuestions.every(q => q.defaultAnswer === 'true' || q.defaultAnswer === 'false')
        : false;

    if (!hasAnswers) return true;

    const hasCustomAnswers =
      (item.texts?.length === 0 || item.texts.every(this.hasAnyAnswer)) &&
      (item.numbers?.length === 0 || item.numbers.every(this.hasAnyAnswer)) &&
      (item.ints?.length === 0 || item.ints.every(this.hasAnyAnswer)) &&
      (item.moneys?.length === 0 || item.moneys.every(this.hasAnyAnswer)) &&
      (item.dropdowns?.length === 0 || item.dropdowns.every(this.hasAnyAnswer)) &&
      (item.radiolists?.length === 0 || item.radiolists.every(this.hasAnyAnswer)) &&
      (item.checklists?.length === 0 || item.checklists.every(this.hasAnyAnswer)) &&
      (item.dates?.length === 0 || item.dates.every(this.hasAnyAnswer)) &&
      (item.dateTimes?.length === 0 || item.dateTimes.every(this.hasAnyAnswer));

    if (!hasCustomAnswers) return true;

    const isPhotoRequired =
      isPhotoVisible &&
      (isPhotoRequiredByLists ||
        (item.emojiDetails?.length === 0
          ? item.photoRequired
          : selectedEmoji?.onSelect?.includes('photoRequired') || false));

    const hasPhotos = isPhotoRequired ? item.fileUploadIds?.length > 0 : true;

    if (!hasPhotos) return true;

    const isCommentRequired =
      isCommentVisible &&
      (isCommentRequiredByLists ||
        (item.emojiDetails?.length === 0
          ? item.commentRequired
          : selectedEmoji?.onSelect?.includes('commentRequired') || false));

    const hasComment = isCommentRequired ? !!(item.comment && item.comment.trim() !== '') : true;

    if (!hasComment) return true;

    return !(hasValue && hasAnswers && hasCustomAnswers && hasPhotos && hasComment);
  }

  private hasAnyAnswer(f: CustomField): boolean {
    return f.isRequired === false
      ? true
      : (f.value !== undefined && f.value !== null) || (f.defaultValue !== undefined && f.defaultValue !== null);
  }

  private getRequirementsBasedOnLists(item: ResidentInspectionItem): {
    isCommentRequiredByLists: boolean;
    isPhotoRequiredByLists: boolean;
  } {
    const listFields = [...(item.checklists || []), ...(item.radiolists || [])];

    const results = listFields.map(listField => {
      const values = this.getSelectedCustomFieldValues(listField);

      return {
        isCommentRequired:
          (values.length > 0 && listField.commentsRequiresForAllitems) ||
          values.some(v => listField.commentsRequiresOn?.includes(v)),
        isPhotoRequired:
          (values.length > 0 && listField.photoRequiresForAllitems) ||
          values.some(v => listField.photoRequiresOn?.includes(v)),
      };
    });

    return {
      isCommentRequiredByLists: results.some(r => r.isCommentRequired),
      isPhotoRequiredByLists: results.some(r => r.isPhotoRequired),
    };
  }

  private getSelectedCustomFieldValues(field: CustomField): string[] {
    const rawValue = field?.value ?? field?.defaultValue;

    if (Array.isArray(rawValue)) {
      return rawValue.map(v => (typeof v === 'string' ? v.trim() : String(v))).filter(v => v !== '');
    }

    if (rawValue === undefined || rawValue === null || rawValue === '') return [];

    return String(rawValue)
      .split(',')
      .map(v => v.trim())
      .filter(v => v !== '');
  }

  generateItemSpecificKey(item: ResidentInspectionItem): string {
    const inspectionId = item.inspectionId;
    const areaId = item.id;
    const itemId = item.uuid;
    return `${LocalStorageDataEnum.UNSAVED_DATA}-${inspectionId}-${areaId}-${itemId}`;
  }
}
